<template>
  <button @click="handleClick">
    <span class="label">VIZBI {{ latestYear }}:</span>
    <span class="timer">{{ timerText }}</span>
  </button>
</template>

<script setup>
import {ref, computed, onUnmounted} from "vue";
import {DateTime} from "luxon";
import {useRouter} from "vue-router";
import useUserSafe from "@/composables/useUserSafe";
import useEventState from "@/composables/useEventState";
import history from "@/assets/history.json";

const router = useRouter();
const {isSignedIn, user} = useUserSafe();
const {nextEventDate} = useEventState();
const latestYear = history.latestYear;

const QUALIFYING_ROLES = ["participant", "organizer", "speaker", "sponsor", "session-chair"];

const now = ref(DateTime.now());
const interval = setInterval(() => {
  now.value = DateTime.now();
}, 1000);
onUnmounted(() => clearInterval(interval));

function isRegistered() {
  const userEvents = user.value?.publicMetadata?.events;
  if (!userEvents) return false;
  return ["workshop", "masterclass", "showcase"].some((type) => {
    const entry = userEvents[`${type}-${latestYear}`];
    if (!entry) return false;
    return Object.values(entry).some(
      (roles) => Array.isArray(roles) && roles.some((r) => QUALIFYING_ROLES.includes(r))
    );
  });
}

function formatCountdown(target, nowVal) {
  if (!target || !nowVal) return "";
  const diff = DateTime.fromJSDate(target).diff(nowVal, ["days", "hours", "minutes", "seconds"]).toObject();
  const d = Math.floor(diff.days || 0);
  const h = Math.floor(diff.hours || 0);
  const m = Math.floor(diff.minutes || 0);
  const s = Math.floor(diff.seconds || 0);
  if (d > 0) return `${d}d ${String(h).padStart(2, "0")}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

const timerText = computed(() => formatCountdown(nextEventDate, now.value));

function handleClick() {
  if (!isSignedIn.value) {
    router.push({hash: "#join"});
    return;
  }
  if (isRegistered()) {
    router.push({name: "Live"});
    return;
  }
  router.push({name: "Registration", params: {year: latestYear}});
}
</script>

<style scoped>
button {
  display: inline-flex;
  gap: 0.4em;
  align-items: baseline;
  color: white;
  background-color: var(--brand-color);
  padding: 0.6rem 2.3rem;
  border: none;
  border-radius: 17px;
  cursor: pointer;
  white-space: nowrap;
}
button:hover { background-color: var(--brand-hover-color); }
.label { font-weight: bold; }
.timer {
  font-weight: 300;
  font-variant-numeric: tabular-nums;
}
</style>
